import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Link as LinkIcon, Check } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authHeaders, formatError } from "../../lib/adminApi";
import { formatSlotIst } from "../../lib/calendar";

const StatusPill = ({ status }) => {
  const map = {
    not_opened: { cls: "bg-slate-100 text-slate-600", label: "Not opened" },
    in_progress: { cls: "bg-[#FFF3ED] text-[#FF5E1A]", label: "In progress" },
    completed: { cls: "bg-emerald-50 text-emerald-700", label: "Completed" },
  };
  const s = map[status] || map.not_opened;
  return <span className={`inline-flex rounded-full ${s.cls} px-2 py-0.5 text-[11px] font-semibold`}>{s.label}</span>;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminProgress() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/invites`, { headers: authHeaders() });
        setInvites(r.data || []);
      } catch (e) { setErr(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const counts = {
    all: invites.length,
    not_opened: invites.filter((i) => i.status === "not_opened").length,
    in_progress: invites.filter((i) => i.status === "in_progress").length,
    completed: invites.filter((i) => i.status === "completed").length,
  };
  const rows = filter === "all" ? invites : invites.filter((i) => i.status === filter);

  const copyLink = async (inv) => {
    const url = `${window.location.origin}/onboard/${inv.cp_name_slug}/${inv.token}`;
    try { await navigator.clipboard.writeText(url); } catch {}
    setCopiedId(inv.id); setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Onboarding Progress</span></div>
        <button onClick={() => navigate("/admin/dashboard")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#FF5E1A]"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="progress-filters">
          {[["all", "All links"], ["not_opened", "Not opened"], ["in_progress", "In progress"], ["completed", "Completed"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`progress-filter-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold ${filter === k ? "bg-[#FF5E1A] text-white" : "bg-white border border-slate-300 text-slate-600"}`}>
              {label} ({counts[k]})
            </button>
          ))}
        </div>

        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        {loading ? <div className="text-slate-500">Loading…</div> : (
          <div className="glass-strong rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-white/60 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3">CP</th>
                  <th className="text-left px-3 py-3">Program</th>
                  <th className="text-left px-3 py-3">Team member</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Opened by</th>
                  <th className="text-left px-3 py-3">Chosen slot</th>
                  <th className="text-left px-3 py-3">First opened</th>
                  <th className="text-left px-3 py-3">Last seen</th>
                  <th className="text-left px-3 py-3">Progress</th>
                  <th className="text-left px-3 py-3">Link</th>
                </tr>
              </thead>
              <tbody data-testid="progress-list">
                {rows.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100" data-testid={`progress-row-${inv.id}`}>
                    <td className="px-3 py-2 font-semibold">{inv.cp_name}</td>
                    <td className="px-3 py-2 text-slate-600">{inv.program_title}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{inv.assigned_to_name || "—"}</td>
                    <td className="px-3 py-2"><StatusPill status={inv.status} /></td>
                    <td className="px-3 py-2 text-xs" data-testid={`progress-viewers-${inv.id}`}>
                      {inv.viewers?.length > 0 ? inv.viewers.map((v) => (
                        <div key={v.id} className="whitespace-nowrap">
                          <span className="font-semibold">{v.name}</span>{" "}
                          <span className={v.completed ? "text-emerald-600 font-semibold" : "text-slate-500"}>{v.completed ? "done" : `${v.progress_percent}%`}</span>
                        </div>
                      )) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs" data-testid={`progress-slot-${inv.id}`}>{inv.chosen_slot ? formatSlotIst(inv.chosen_slot) : "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmt(inv.first_opened_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmt(inv.last_seen_at)}</td>
                    <td className="px-3 py-2 text-xs">{inv.progress_percent}% <span className="text-slate-400">({inv.completed_count}/{inv.total_steps})</span></td>
                    <td className="px-3 py-2">
                      <button onClick={() => copyLink(inv)} data-testid={`progress-copy-${inv.id}`} title="Copy invite link"
                        className="p-1.5 rounded-lg text-slate-500 hover:text-[#2A5CB8] hover:bg-white">
                        {copiedId === inv.id ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan="9" className="px-4 py-6 text-center text-slate-500">No links in this view yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
